import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withTranslation } from "react-i18next";
import { Form, Input, Button, Typography, Modal, message, Card, Cascader, Radio } from 'antd';

import ExecuteRequest from 'components/adapter/unified/ExecuteRequest';
import ExecuteResponse from 'components/adapter/unified/ExecuteResponse';

import * as types from 'reducer/actionTypes';
import { isEmpty } from 'script/common';
import { initResourceType, findValueReplace, findValueInSource, updatedSetValue, setFormFieldValue} from 'script/uniAdapter';

const AdapterGet = ({ t, webofficeURLParam, adapterName, tabName }) => {
	const interfaceName = "get"
	const uniData = useSelector(state => state.uniAdapter?.[interfaceName]);
	const responseSource = useSelector(state => state.uniAdapter?.[interfaceName]?.responseSource);
	const setValue = useSelector(state => state.uniAdapter?.[interfaceName]?.value ?? [{ key:'binaryData', value:'', resource: initResourceType}]);
	const isRequestActive = useSelector(state => state.uniAdapter?.[interfaceName]?.isRequestActive);
	const requestInit = useSelector(state => state.uniAdapter.requestInit);
	/** ========================================================================================================================================== **/
	const dispatch = useDispatch();
	const fetchSetUniAdapterInterface = (value) => {
		dispatch({type: types.FETCH_SET_UNIADAPTER_INTERFACE_REQUEST, interfaceType: interfaceName, value:value})
	};
	/** ========================================================================================================================================== **/
	const [ form ] = Form.useForm(); 
	const { Title } = Typography;
	const isSettingForm = uniData.response.status || !uniData.isRequestActive;
	
	useEffect(() => {
		if (isSettingForm) {
			if (!isEmpty(setValue)) {
				form.setFieldsValue({ setValue: updatedSetValue(setValue, responseSource) });
			}
		}
	}, [responseSource, isSettingForm, setValue, form]);

	// Save Setting
	const handleSave = () => {
		if (isRequestActive === false) {
			Modal.confirm({
				title: "Are you sure you want to save without any requests?",
				onOk: () => {
					fetchSave();
				},
				onCancel: () => {
					message.info("The save has been canceled.");
				},
			});
		} else {
			fetchSave();
		}
	};

	const fetchSave = () => {
		let updatedSetValue = null;
		let isValid = true;

		const setValue = form.getFieldsValue().setValue;

		if (!isEmpty(setValue)) {
			//MongoDB data to save - remove valueIsValid
			updatedSetValue = setValue.map((data) => {
				// 빈 값인 경우에도 저장되도록 임시
				// if (isEmpty(data.value)) isValid = false;

				const { valueIsValid, ...rest } = data;
				return {
					...rest,
				};
			});
		}

		if (isValid){
			fetchSetUniAdapterInterface({
				interfaceType: interfaceName,
				isRequestActive: isRequestActive,
				request: (isRequestActive ? uniData.request : requestInit),
				value: updatedSetValue, 
				adapterName: adapterName
			 });
		} else {
			message.error("'binaryData' value is empty");
		}
	}

	return (
		<div style={{ padding: '20px', margin: 'auto' }}>
			<Title level={2}>{interfaceName.toUpperCase()}</Title>

			{/* Request */}
			<ExecuteRequest
				interfaceType = {interfaceName}
				webofficeURLParam={webofficeURLParam}
				adapterName={adapterName}
				tabName= {tabName}
			/>

			{/* Response */}
			{(uniData.response.status) && (
				<ExecuteResponse
					interfaceType = {interfaceName}
					webofficeURLParam={webofficeURLParam}
				/>
			)}

			{/* Save Setting */}
			{(isSettingForm) && (
				<Card
					title="Binary contents of the file"
					type="inner"
					className="uniAdapter-card-blue"
					style={{ marginTop: 20 }}
				>
					<Form
						form={form}
						layout="vertical"
					>
						<Form.List name="setValue">
							{(fields, {}) => (
								<div style={{ display: "flex", rowGap: 16, flexDirection: "column"}}>
									{fields.map((field, index) => {
										return (
											<Card
												size="small"
												key={field.key}
											>

												{/* key */}
												<Typography.Title level={5}>Key</Typography.Title>

												<div style={{ alignItems: 'center', gap: '6px' }}>
													<Form.Item 
														name={[field.name, 'key']}
														validateTrigger="onChange"
													>
														<Input placeholder="Insert key" readOnly={true} bordered={false}/>
													</Form.Item>
												</div>

												{/* value */}
												<Typography.Title level={5}>Value</Typography.Title>
												<div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: 10 }}>
													{/* 1. select resource type  */}
													<Form.Item 
														name={[ field.name, 'resource' ]}
														style={{ marginBottom: 0 }}
														onChange={(e) => {
															const { value } = e.target;
															setFormFieldValue(form, index, 'resource', value)
														}}
													>
														<Radio.Group
															placeholder="Select response source"
														>
															<Radio.Button value="store">Store</Radio.Button>
															<Radio.Button value="customized">Customization</Radio.Button>
														</Radio.Group>
													</Form.Item>

													{/* 2. insert resource value */}
													<Form.Item
														name={[ field.name, 'value' ]}
														style={{ marginBottom: 0, flex: 1 }}
													>
														{form.getFieldValue(['setValue', field.name, 'resource']) === initResourceType ?
														(
															<Cascader
																placeholder="Select key from source"
																displayRender={(label) => label.join('.')} // 화면에 '.'으로 구분된 형식으로 표시
																options={responseSource}
																multiple={false}
																onChange={(value) => {
																	const valueIsValid = findValueInSource(responseSource, value);
																	const _value = isEmpty(value) ? '' : `{${ value.join('.') }}`
																	setFormFieldValue(form, index, 'value', _value);
																	setFormFieldValue(form, index, 'valueIsValid', valueIsValid);
																}}
															/>
														)
														:
														(
															<Input
																placeholder="Enter custom values"
																onChange={(e) => {
																	const { value } = e.target;
																	const valueIsValid = findValueReplace(responseSource, value);
																	setFormFieldValue('value', value);
																	setFormFieldValue('valueIsValid', valueIsValid);
																}}
															/>

														)
													}
													</Form.Item>
												</div>
												{/* 3. validate - insert resource value */}
												<div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 10}}>
													<Form.Item name={[field.name, 'valueIsValid']} style={{ marginBottom: 0, flex: 1 }}>
														<Input disabled={true} style={{border: 'none', fontWeight: 'bolder'}}/>
													</Form.Item>
												</div>
											</Card>
										)
									})}
								</div>
							)}
						</Form.List>

						<Form.Item>
							<Button type="primary" onClick={handleSave} style={{ marginTop:10 }}> Save </Button>
						</Form.Item>
					</Form>
				</Card>
			)}
			
		</div>
	);
}

export default AdapterGet;